import { useState, useRef } from 'react';
import {
  DrawerRoot,
  DrawerContent,
  DrawerHeader,
  DrawerBody,
  DrawerTitle,
  DrawerCloseTrigger,
} from '@/components/ui/drawer';
import { Button, Flex } from '@chakra-ui/react';
import { FaEdit, FaSave, FaTimes } from 'react-icons/fa';
import { dataTableButtonStyles } from '@/components/data-table';
import { useGetOrganization } from '../organizations.hook';
import { OrganizationEditForm } from './organization-edit-form';

interface OrganizationDetailsDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  organizationId: string | null | undefined;
}

export const OrganizationDetailsDrawer = ({
  isOpen,
  onClose,
  organizationId,
}: OrganizationDetailsDrawerProps) => {
  const [drawerMode, setDrawerMode] = useState<'view' | 'edit'>('view');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const formRef = useRef<{ submit: () => void; reset: () => void } | null>(
    null,
  );

  const { data: organization } = useGetOrganization(organizationId);

  const handleSuccess = () => {
    setDrawerMode('view');
    setIsSubmitting(false);
    // The drawer will automatically refresh due to React Query cache invalidation
  };

  const handleCancel = () => {
    formRef.current?.reset();
    setDrawerMode('view');
  };

  const handleEdit = () => {
    setDrawerMode('edit');
  };

  const handleSave = () => {
    formRef.current?.submit();
  };

  const handleClose = () => {
    setDrawerMode('view');
    onClose();
  };

  return (
    <DrawerRoot
      open={isOpen}
      onOpenChange={({ open }) => !open && handleClose()}
      size="md"
      placement="end"
      closeOnInteractOutside={false}
    >
      <DrawerContent>
        <DrawerHeader>
          <Flex justify="flex-start" align="center" gap={2}>
            <DrawerTitle>
              {drawerMode === 'view'
                ? 'Organization Details'
                : 'Edit Organization'}
            </DrawerTitle>
            <Flex gap={2} align="center">
              {drawerMode === 'edit' ? (
                <>
                  <Button
                    {...dataTableButtonStyles.outline}
                    onClick={handleCancel}
                    disabled={isSubmitting}
                    size="sm"
                  >
                    <FaTimes />
                    Cancel
                  </Button>
                  <Button
                    {...dataTableButtonStyles.solid}
                    onClick={handleSave}
                    loading={isSubmitting}
                    disabled={isSubmitting}
                    size="sm"
                  >
                    <FaSave />
                    Save
                  </Button>
                </>
              ) : (
                <Button
                  {...dataTableButtonStyles.outline}
                  onClick={handleEdit}
                  size="sm"
                >
                  <FaEdit />
                  Edit
                </Button>
              )}
              <DrawerCloseTrigger />
            </Flex>
          </Flex>
        </DrawerHeader>

        <DrawerBody>
          {organization && (
            <OrganizationEditForm
              ref={formRef}
              organization={organization}
              onSuccess={handleSuccess}
              isViewMode={drawerMode === 'view'}
              onSubmittingChange={setIsSubmitting}
            />
          )}
        </DrawerBody>
      </DrawerContent>
    </DrawerRoot>
  );
};
